import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { extractApiError } from "@/lib/api-errors"
import { CalThrottledNotice } from "@/components/CalThrottledNotice"
import { isCalThrottled } from "@/lib/calThrottled"
import { useGetResolveCapabilitiesQuery } from "@/app/appointments/schedule-overrides/scheduleOverridesApi"
import {
  useGetAdminRescheduleOptionsQuery,
  useRescheduleAsAdminMutation,
} from "../../api/rescheduleApi"
import type { AdminRescheduleDay } from "../../api/rescheduleApi"
import { SITE_TIME_ZONE, longDateLabel, minutesToLabel, zonedDateAndMinutes } from "../../lib/siteTime"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { describeRescheduleFailure, isRescheduleReasonValid } from "./RescheduleAction"
import type { BookingAction, BookingActionFormProps } from "./types"
import { MAX_NOTE_LENGTH, isChangeable } from "./types"
import { useReportBusy } from "./useReportBusy"

/**
 * How far ahead the server offers times — AdminRescheduleRules.HorizonDays, the 28-day booking window.
 * The fallback only: with the week view on, the form reads the server's own `lookaheadDays` (ams-en1).
 */
export const ADMIN_RESCHEDULE_HORIZON_DAYS = 28

/** One start as the grouped picker shows it: the server's own `start`, and who would take it. */
export interface AdminRescheduleChoice {
  /** The picker's key, and the start sent back exactly as it came. */
  value: string
  /** "9:00 AM · Cora Current" — the time in the office's zone, and the host the server would pick. */
  label: string
  /** Whether that host is the one the appointment is with now. */
  keepsHost: boolean
}

/** One Pacific day of the picker. */
export interface AdminRescheduleGroup {
  /** "2026-10-07". */
  date: string
  /** "Wednesday, Oct 7". */
  label: string
  choices: AdminRescheduleChoice[]
}

/**
 * The server's days as the picker's groups, in the order it sent them. Each time is labelled in the
 * office's zone, whatever the browser's, with the host the server would give it to — the first it
 * lists, which is the current host whenever they are free. A day with no starts is left out.
 */
export function adminRescheduleGroups(
  days: readonly AdminRescheduleDay[],
  timeZone: string = SITE_TIME_ZONE,
): AdminRescheduleGroup[] {
  return days
    .filter(day => day.starts.length > 0)
    .map(day => ({
      date: day.date,
      label: longDateLabel(day.date),
      choices: day.starts.map(start => {
        const host = start.hosts[0]
        const time = minutesToLabel(zonedDateAndMinutes(start.start, timeZone).minutes)
        return {
          value: start.start,
          label: host ? `${time} · ${host.name}` : time,
          keepsHost: host?.isCurrent === true,
        }
      }),
    }))
}

/**
 * A failed admin reschedule in words. The server's message comes first — only it knows whether the
 * household still has the old appointment, and its 502s say so plainly. Then the status.
 */
export function describeAdminRescheduleFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)
  if (detail) return detail
  if (failure?.status === 403) return "Only a manager can reschedule an appointment to another time."
  return describeRescheduleFailure(error)
}

/**
 * A manager moving an appointment to any free time of its own slot type at its own site, in the next
 * 28 days (AB#610). The server picks the host — the current one if free, else another free one — and
 * re-checks everything; the form only shows what it offers. Labelled apart from the host's own
 * "Reschedule", which a manager who is also the host still sees.
 */
export const AdminRescheduleAction: BookingAction = {
  id: "adminRescheduling",
  label: "Reschedule (admin)",
  triggerVariant: "outline",
  isAvailable: ctx => isChangeable(ctx) && ctx.canManage,
  Form: AdminRescheduleForm,
}

function AdminRescheduleForm({ ctx, onClose, onDone, onBusyChange }: BookingActionFormProps) {
  // Asked only here, when the form opens: it is a 28-day read of the whole site's roster.
  const options = useGetAdminRescheduleOptionsQuery(ctx.uid, { refetchOnMountOrArgChange: true })
  // The window's length as the server holds it (resolve/capabilities, AB#810); 28 until it answers.
  const capabilities = useGetResolveCapabilitiesQuery()
  const horizonDays = capabilities.currentData?.lookaheadDays ?? ADMIN_RESCHEDULE_HORIZON_DAYS
  const [reschedule, rescheduleState] = useRescheduleAsAdminMutation()

  const [chosenStart, setChosenStart] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  /** Cal throttled the save (503 calThrottled, AB#514): the busy notice instead of `error`. */
  const [throttled, setThrottled] = useState<unknown>(null)

  const busy = rescheduleState.isLoading
  useReportBusy(onBusyChange, busy)

  const allowed = options.data?.allowed === true
  const groups = adminRescheduleGroups(options.data?.days ?? [], SITE_TIME_ZONE)
  const chosen = groups.flatMap(group => group.choices).find(choice => choice.value === chosenStart)
  const canSubmit = allowed && !!chosen && isRescheduleReasonValid(reason) && !busy && !warning

  async function submit() {
    if (!canSubmit || !chosen) return
    setError(null)
    setThrottled(null)

    const result = await reschedule({
      uid: ctx.uid,
      requestId: ctx.requestId,
      newStart: chosen.value,
      reason: reason.trim(),
    })

    if ("error" in result) {
      if (isCalThrottled(result.error)) {
        setThrottled(result.error)
        return
      }
      setError(describeAdminRescheduleFailure(result.error))
      return
    }

    // Moved: the uid this dialog was opened on is cancelled. Stay only for something staff must read.
    if (result.data.message) {
      setWarning(result.data.message)
      return
    }

    onClose()
  }

  return (
    <>
      <section>
        {options.isLoading || options.isFetching ? (
          <p className="text-sm text-muted-foreground">Finding free times…</p>
        ) : options.isError && isCalThrottled(options.error) ? (
          <CalThrottledNotice error={options.error} onRetry={() => void options.refetch()} />
        ) : options.isError ? (
          <p role="alert" className="text-sm text-red-700">
            {extractApiError((options.error as { data?: unknown } | undefined)?.data)
              || "Could not find free times. Try again in a moment."}
          </p>
        ) : !allowed ? (
          <p role="alert" className="text-sm text-red-700">
            {options.data?.deniedReason || "This appointment cannot be rescheduled."}
          </p>
        ) : (
          <>
            <Label htmlFor="admin-reschedule-start" className="text-xs font-medium">
              New time <span className="text-muted-foreground">(same slot type and site, Pacific time)</span>
            </Label>
            {groups.length === 0 ? (
              <p className="mt-1.5 text-sm text-muted-foreground">
                There are no free times for this slot type at this site in the next {horizonDays} days.
              </p>
            ) : (
              <Select value={chosenStart} onValueChange={value => { setChosenStart(value); setError(null) }} disabled={busy || !!warning}>
                <SelectTrigger id="admin-reschedule-start" className="mt-1.5 w-full">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(group => (
                    <SelectGroup key={group.date}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.choices.map(choice => (
                        <SelectItem key={choice.value} value={choice.value}>{choice.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            )}
            {chosen && !chosen.keepsHost && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                The current host is not free then, so it moves to the host shown.
              </p>
            )}

            <Label htmlFor="admin-reschedule-reason" className="mt-3 block text-xs font-medium">
              Reason <span className="text-muted-foreground">(required; internal, never sent to the household)</span>
            </Label>
            <Textarea
              id="admin-reschedule-reason"
              className="mt-1.5"
              rows={2}
              maxLength={MAX_NOTE_LENGTH}
              value={reason}
              disabled={busy || !!warning}
              placeholder="e.g. Host is at training that morning"
              onChange={event => setReason(event.target.value)}
            />

            <p className="mt-3 text-xs text-muted-foreground">
              {ctx.attendeeName ?? "The attendee"} will be told the new time by CMS. Their confirmation
              number still works, but the booking reference changes.
            </p>
          </>
        )}
      </section>

      {throttled ? (
        <CalThrottledNotice error={throttled} onRetry={() => void submit()} />
      ) : error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {warning && <p role="alert" className="text-sm text-amber-700">{warning}</p>}

      <AppointmentDialogFooter>
        {warning ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="outline" disabled={busy} onClick={onDone}>Cancel</Button>
            {allowed && (
              <Button disabled={!canSubmit} onClick={submit}>
                {busy ? "Rescheduling…" : "Reschedule"}
              </Button>
            )}
          </>
        )}
      </AppointmentDialogFooter>
    </>
  )
}
